"use client";

import { useEffect, useMemo, useState } from "react";
import type {
  AskUserQuestion,
  AskUserQuestionAnswer,
  AskUserQuestionResponse,
  AskUserQuestionOption,
} from "@tracify/shared";
import { Button } from "@tracify/ui/button";
import { cn } from "@/lib/utils";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import type { PendingAskUserQuestion } from "./ask-user-question";

type AskUserQuestionPanelProps = {
  question: PendingAskUserQuestion;
  isSubmitting?: boolean;
  onSubmit: (input: {
    toolCallId: string;
    output: AskUserQuestionResponse;
  }) => void;
};

const CUSTOM_SELECTION = "__custom__";

type QuestionDraft = {
  selection: string | null;
  otherAnswer: string;
};

function getInitialSelection(question: AskUserQuestion, questionCount: number) {
  if ((question.options?.length ?? 0) > 0) {
    return questionCount === 1
      ? (question.options?.[0]?.id ?? CUSTOM_SELECTION)
      : null;
  }

  return CUSTOM_SELECTION;
}

function createInitialDrafts(questionSession: PendingAskUserQuestion) {
  return Object.fromEntries(
    questionSession.questions.map((item) => [
      item.id,
      {
        selection: getInitialSelection(item, questionSession.questions.length),
        otherAnswer: "",
      } satisfies QuestionDraft,
    ])
  );
}

function buildOptionAnswer(
  question: AskUserQuestion,
  option: AskUserQuestionOption
): AskUserQuestionAnswer {
  return {
    question_id: question.id,
    question: question.question,
    response_type: "option",
    answer_text: option.label,
    option_id: option.id,
    option_label: option.label,
  };
}

function getSelectedOption(
  question: AskUserQuestion,
  draft: QuestionDraft | undefined
) {
  if (!draft?.selection || draft.selection === CUSTOM_SELECTION) {
    return null;
  }

  return (
    question.options?.find((option) => option.id === draft.selection) ?? null
  );
}

function isQuestionAnswered(
  question: AskUserQuestion,
  draft: QuestionDraft | undefined
) {
  if (!draft) {
    return false;
  }

  if (draft.selection === CUSTOM_SELECTION) {
    return draft.otherAnswer.trim().length > 0;
  }

  return Boolean(getSelectedOption(question, draft));
}

function buildAnswer(
  question: AskUserQuestion,
  draft: QuestionDraft | undefined
) {
  if (!draft) {
    return null;
  }

  const selectedOption = getSelectedOption(question, draft);
  if (selectedOption) {
    return buildOptionAnswer(question, selectedOption);
  }

  if (
    draft.selection === CUSTOM_SELECTION &&
    draft.otherAnswer.trim().length > 0
  ) {
    return {
      question_id: question.id,
      question: question.question,
      response_type: "other" as const,
      answer_text: draft.otherAnswer.trim(),
    };
  }

  return null;
}

export function AskUserQuestionPanel({
  question,
  isSubmitting = false,
  onSubmit,
}: AskUserQuestionPanelProps) {
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);
  const [drafts, setDrafts] = useState<Record<string, QuestionDraft>>(() =>
    createInitialDrafts(question)
  );

  useEffect(() => {
    setActiveQuestionIndex(0);
    setDrafts(createInitialDrafts(question));
    // A new tool call starts a new form. Updates to the same call must preserve
    // in-progress answers rather than resetting local state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question.toolCallId]);

  const activeQuestion = question.questions[activeQuestionIndex];
  const activeDraft = activeQuestion ? drafts[activeQuestion.id] : undefined;
  const isCustomSelected = activeDraft?.selection === CUSTOM_SELECTION;
  const isCurrentQuestionAnswered = activeQuestion
    ? isQuestionAnswered(activeQuestion, activeDraft)
    : false;
  const hasUnansweredQuestions = useMemo(
    () =>
      question.questions.some(
        (item) => !isQuestionAnswered(item, drafts[item.id])
      ),
    [drafts, question.questions]
  );
  const canGoPrevious = activeQuestionIndex > 0;
  const canGoNext = activeQuestionIndex < question.questions.length - 1;
  const isFinalQuestion = !canGoNext;
  const isPrimaryActionDisabled =
    isSubmitting ||
    !isCurrentQuestionAnswered ||
    (isFinalQuestion && hasUnansweredQuestions);

  const setQuestionDraft = (
    questionId: string,
    nextDraft: Partial<QuestionDraft>
  ) => {
    setDrafts((current) => ({
      ...current,
      [questionId]: {
        selection: current[questionId]?.selection ?? null,
        otherAnswer: current[questionId]?.otherAnswer ?? "",
        ...nextDraft,
      },
    }));
  };

  const moveQuestion = (direction: -1 | 1) => {
    setActiveQuestionIndex((current) => {
      const nextIndex = current + direction;
      if (nextIndex < 0 || nextIndex >= question.questions.length) {
        return current;
      }

      return nextIndex;
    });
  };

  const buildAnswers = (nextDrafts: Record<string, QuestionDraft>) =>
    question.questions
      .map((item) => buildAnswer(item, nextDrafts[item.id]))
      .filter((item): item is AskUserQuestionAnswer => item !== null);

  const handleSubmit = (nextDrafts = drafts) => {
    if (isSubmitting) {
      return;
    }

    const answers = buildAnswers(nextDrafts);

    if (answers.length !== question.questions.length) {
      return;
    }

    onSubmit({
      toolCallId: question.toolCallId,
      output: {
        approved: true,
        answers,
      },
    });
  };

  const handleOptionSelect = (optionId: string) => {
    if (!activeQuestion || isSubmitting) {
      return;
    }

    const nextDrafts = {
      ...drafts,
      [activeQuestion.id]: {
        selection: optionId,
        otherAnswer: drafts[activeQuestion.id]?.otherAnswer ?? "",
      },
    };
    setDrafts(nextDrafts);

    if (isFinalQuestion) {
      handleSubmit(nextDrafts);
      return;
    }

    moveQuestion(1);
  };

  const handleDismiss = () => {
    if (isSubmitting) {
      return;
    }

    onSubmit({
      toolCallId: question.toolCallId,
      output: {
        approved: false,
      },
    });
  };

  const handlePrimaryAction = () => {
    if (isSubmitting) {
      return;
    }

    if (isFinalQuestion) {
      handleSubmit();
      return;
    }

    moveQuestion(1);
  };

  if (!activeQuestion) {
    return null;
  }

  return (
    <div
      className="flex flex-col gap-2 pt-1"
      data-testid="ask-user-question-panel"
      onKeyDownCapture={(event) => {
        if (isSubmitting) {
          return;
        }

        if (event.key === "Escape") {
          event.preventDefault();
          handleDismiss();
          return;
        }

        if (
          event.target instanceof HTMLInputElement ||
          event.target instanceof HTMLTextAreaElement
        ) {
          return;
        }

        if (event.key === "Enter") {
          if (event.target instanceof HTMLButtonElement) {
            const action = event.target.dataset.askUserAction;
            if (
              action === "previous" ||
              action === "next" ||
              action === "submit" ||
              action === "dismiss"
            ) {
              return;
            }

            if (
              action === "option" &&
              event.target.dataset.optionId &&
              activeDraft?.selection !== event.target.dataset.optionId
            ) {
              return;
            }
          }

          if (isPrimaryActionDisabled) {
            return;
          }

          event.preventDefault();
          handlePrimaryAction();
          return;
        }

        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") {
          return;
        }

        event.preventDefault();

        if (event.key === "ArrowLeft") {
          moveQuestion(-1);
          return;
        }

        moveQuestion(1);
      }}
    >
      <div className="flex items-start justify-between gap-2 px-2">
        <div
          className="pr-2 text-sm font-semibold text-foreground"
          data-testid="ask-user-question-title"
        >
          {activeQuestion.question}
        </div>

        <div className="flex shrink-0 items-center gap-0.5 text-xs text-muted-foreground">
          <button
            type="button"
            onClick={() => moveQuestion(-1)}
            disabled={isSubmitting || !canGoPrevious}
            data-ask-user-action="previous"
            className="inline-flex size-5 items-center justify-center rounded-md transition-colors hover:bg-muted/40 disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent"
            aria-label="Previous question"
            data-testid="ask-user-question-prev"
          >
            <ChevronLeftIcon className="size-3.5" />
          </button>
          <div
            className="min-w-10 text-center tabular-nums"
            data-testid="ask-user-question-index"
          >
            {activeQuestionIndex + 1} of {question.questions.length}
          </div>
          <button
            type="button"
            onClick={() => moveQuestion(1)}
            disabled={isSubmitting || !canGoNext}
            data-ask-user-action="next"
            className="inline-flex size-5 items-center justify-center rounded-md transition-colors hover:bg-muted/40 disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent"
            aria-label="Next question"
            data-testid="ask-user-question-next"
          >
            <ChevronRightIcon className="size-3.5" />
          </button>
        </div>
      </div>

      <div className="mt-2 space-y-1">
        {activeQuestion.options?.map((option, index) => {
          const isSelected = activeDraft?.selection === option.id;

          return (
            <button
              key={option.id}
              type="button"
              disabled={isSubmitting}
              onClick={() => handleOptionSelect(option.id)}
              data-ask-user-action="option"
              data-option-id={option.id}
              data-testid={`ask-user-option-${option.id}`}
              aria-pressed={isSelected}
              className={cn(
                "flex w-full items-start gap-1 rounded-xl px-2 py-1 text-left transition-colors",
                isSelected
                  ? "bg-muted/70 text-foreground"
                  : "text-foreground/80 hover:bg-muted/30"
              )}
            >
              <span
                className={cn(
                  "w-6 shrink-0 pt-0.5 text-sm tabular-nums",
                  isSelected
                    ? "font-semibold text-foreground"
                    : "text-muted-foreground"
                )}
              >
                {index + 1}.
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold leading-5 text-foreground">
                  {option.label}
                </span>
                {option.description ? (
                  <span className="mt-0.5 block text-sm leading-5 text-muted-foreground">
                    {option.description}
                  </span>
                ) : null}
              </span>
            </button>
          );
        })}

        {activeQuestion.allow_other ? (
          <div
            className={cn(
              "flex items-start gap-1 rounded-xl px-2 py-1 transition-colors",
              isCustomSelected
                ? "bg-muted/70 text-foreground"
                : "text-foreground/80 hover:bg-muted/30"
            )}
          >
            <span
              className={cn(
                "w-6 shrink-0 pt-0.5 text-sm tabular-nums",
                isCustomSelected
                  ? "font-semibold text-foreground"
                  : "text-muted-foreground"
              )}
            >
              {((activeQuestion.options?.length ?? 0) + 1).toString()}.
            </span>
            <input
              type="text"
              value={activeDraft?.otherAnswer ?? ""}
              disabled={isSubmitting}
              onFocus={() =>
                setQuestionDraft(activeQuestion.id, {
                  selection: CUSTOM_SELECTION,
                })
              }
              onChange={(event) => {
                setQuestionDraft(activeQuestion.id, {
                  selection: CUSTOM_SELECTION,
                  otherAnswer: event.target.value,
                });
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !isPrimaryActionDisabled) {
                  event.preventDefault();
                  handlePrimaryAction();
                }
              }}
              placeholder={activeQuestion.placeholder ?? "Type a custom answer"}
              className="h-6 w-full bg-transparent px-0 text-sm font-semibold leading-6 text-foreground outline-none ring-0 border-none placeholder:text-muted-foreground"
              data-testid="ask-user-other-input"
            />
          </div>
        ) : null}
      </div>

      <div className="mt-3 flex justify-end gap-2">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={handleDismiss}
          disabled={isSubmitting}
          data-ask-user-action="dismiss"
          data-testid="ask-user-dismiss"
          className="gap-2 text-muted-foreground hover:text-foreground"
        >
          <span>Dismiss</span>
          <span className="rounded-md bg-muted px-1.5 py-0.5 text-[11px] font-semibold leading-none text-muted-foreground">
            ESC
          </span>
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={handlePrimaryAction}
          disabled={isPrimaryActionDisabled}
          data-ask-user-action="submit"
          data-testid="ask-user-submit"
        >
          {isSubmitting
            ? "Continuing..."
            : isFinalQuestion
              ? (question.submit_label ?? "Submit")
              : "Continue"}
        </Button>
      </div>
    </div>
  );
}
